import { useCallback } from 'react';
import { useFax } from './FaxContext';
import { useEmail } from '../Email/EmailContext';

/**
 * Returns `{ print, fax, email }` handlers for a given print promise factory.
 *
 * @param getPrintPromise - Called with `(faxInstructions?, emailInstructions?)` to execute the print job.
 * @param onBefore - Optional; called before starting (e.g. setLoading(true)).
 * @param onAfter - Optional; called when the request settles (e.g. setLoading(false)).
 */
export function usePrintDispatch(getPrintPromise, onBefore, onAfter) {
  const {
    submitPrintOrFax
  } = useFax();
  const {
    submitEmail
  } = useEmail();
  const print = useCallback(() => {
    if (onBefore) onBefore();
    submitPrintOrFax({
      wantFax: false,
      getPrintPromise: faxInstructions => getPrintPromise(faxInstructions, undefined)
    }).finally(() => {
      if (onAfter) onAfter();
    }) // eslint-disable-next-line @typescript-eslint/no-empty-function
    .catch(() => {});
  }, [submitPrintOrFax, getPrintPromise, onBefore, onAfter]);
  const fax = useCallback(() => {
    if (onBefore) onBefore();
    submitPrintOrFax({
      wantFax: true,
      getPrintPromise: faxInstructions => getPrintPromise(faxInstructions, undefined)
    }).finally(() => {
      if (onAfter) onAfter();
    }) // eslint-disable-next-line @typescript-eslint/no-empty-function
    .catch(() => {});
  }, [submitPrintOrFax, getPrintPromise, onBefore, onAfter]);
  const email = useCallback(() => {
    if (onBefore) onBefore();
    submitEmail({
      wantEmail: true,
      getPrintPromise: emailInstructions => getPrintPromise(undefined, emailInstructions)
    }).finally(() => {
      if (onAfter) onAfter();
    }) // eslint-disable-next-line @typescript-eslint/no-empty-function
    .catch(() => {});
  }, [submitEmail, getPrintPromise, onBefore, onAfter]);
  return {
    print,
    fax,
    email
  };
}